import {
	alwaysRefetchApiQueryOptions,
	type IUseAccessCredentialsRequest,
	type IUseVerifyTransaction,
	type IUseVerifyTransactionProps,
	OTPLENGTH,
} from 'BreetHelpers';
import { useAccessCredentials, useModalApi } from 'BreetHooks';
import { useGetUserPreferenceQuery } from 'BreetRedux';
import { useCallback } from 'react';

export const useVerifyTransaction = ({
	submitRequest,
	closeModalId,
	description,
}: IUseVerifyTransactionProps): IUseVerifyTransaction => {
	const { data: userPreferenceData, isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const { closeModalDirect } = useModalApi();

	const { handleVerification: handleOtpVerification } = useAccessCredentials({
		id: '2fa-step',
		mode: 'otp',
		submitRequest,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify OTP',
		description: description ?? `Enter the ${OTPLENGTH}-digit OTP sent to your email to confirm withdrawal`,
		type: 'collect',
		persistOnVerify: true,
		generateOTP: false,
	});

	const handle2faCallback = useCallback(() => {
		// close PIN modal loading dialog
		closeModalDirect('loadingDialog');
		handleOtpVerification();
	}, [closeModalDirect, handleOtpVerification]);

	const handleRequest = useCallback(
		async (data?: IUseAccessCredentialsRequest) => {
			const is2faEnabled = !!userPreferenceData?.data?.is2FAActive || !!userPreferenceData?.data?.isAuthenticator2FaActive;

			await submitRequest(data, is2faEnabled ? handle2faCallback : undefined);
		},
		[handle2faCallback, submitRequest, userPreferenceData?.data?.is2FAActive, userPreferenceData?.data?.isAuthenticator2FaActive]
	);

	const { handleVerification: handlePinVerification } = useAccessCredentials({
		id: 'withdraw-request',
		mode: 'pin',
		submitRequest: handleRequest,
		closeModalId,
		dialogMessage: 'Processing request...',
		type: 'collect',
	});

	return {
		userPreferenceData,
		userPreferenceLoading,
		handleVerification: handlePinVerification,
	};
};
